import { lazy, Suspense, useEffect, useState } from "react";
import type { DashboardData, Transaction } from "../../types";
import type { ReceiptDraft, TransactionReceipts } from "../../types/receipt";
import { api } from "../../lib/api";
import { isExpense } from "../../lib/finance";
import { ReceiptTracking } from "./ReceiptTracking";
import TransactionReceiptForm from "./TransactionReceiptForm";
import PhotoViewer from "./PhotoViewer";

const ReceiptCategoryBreakdown = lazy(() => Promise.all([
  import("./ReceiptCategoryBreakdown"), import("../../styles/14a-receipt-category-split.css"),
]).then(([module]) => module));

/** Receipt evidence is fetched on demand, including every receipt linked to the transaction. */
export default function ReceiptDetails({ transactionId, transaction, disabled = false, onBusyChange, onChanged, onUseDetails, entryOptions }: {
  transactionId: string;
  transaction?: Transaction;
  disabled?: boolean;
  onBusyChange?: (busy: boolean) => void;
  onChanged?: () => void;
  onUseDetails?: (draft: ReceiptDraft) => void;
  entryOptions?: DashboardData["entryOptions"];
}) {
  const [value, setValue] = useState<TransactionReceipts | null>(null);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState<number | null>(null);
  const [form, setForm] = useState<{ receiptId?: string } | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let active = true;
    setError("");
    api<TransactionReceipts>(`receipts?transactionId=${encodeURIComponent(transactionId)}`)
      .then(result => { if (active) setValue(result); })
      .catch(() => { if (active) setError("Receipt details could not load. Try again."); });
    return () => { active = false; };
  }, [transactionId, reload]);
  const receipts = value?.receipts || (value?.receipt || value?.images?.length ? [value] : []);
  const images = value?.images || [];
  return <>
    <section className="receipt-photos-block" aria-label="Receipts">
      <div className="linked-prices-head">
        <h3>Receipts</h3>
        {transaction && !form && <button type="button" disabled={disabled} onClick={() => setForm({})}>Attach receipt</button>}
      </div>
      {error && <p className="form-error" role="alert">{error} <button type="button" onClick={() => setReload(current => current + 1)}>Retry</button></p>}
      {value && !receipts.length && !form && <p className="muted">No receipts attached yet.</p>}
      {!!images.length && <div className="receipt-photos">
        {images.map((id, index) => <button key={id} type="button" className="receipt-photo-button"
          aria-label={`View receipt photo ${index + 1}`} onClick={() => setViewing(index)}>
          <img src={`/api/receipts/image?id=${encodeURIComponent(id)}&size=thumb`} alt="Receipt photo" loading="lazy" />
        </button>)}
      </div>}
    </section>
    {viewing !== null && !!images.length && <PhotoViewer images={images} start={viewing}
      title={transaction?.title || value?.receipt?.expense.title || "Receipt"}
      filename={["receipt", transaction?.title, transaction?.date.slice(0, 10)].filter(Boolean).join(" ")}
      onClose={() => setViewing(null)} />}
    {receipts.map((saved, index) => <section key={saved.id || saved.receipt?.id || index}>
      {saved.receipt && !!saved.receipt.items.length && <details className="receipt-details">
        <summary>Receipt items & price tracking ({saved.receipt.items.length}){receipts.length > 1 ? ` · Receipt ${index + 1}` : ""}</summary>
        {(!transaction || isExpense(transaction)) ? <ReceiptTracking draft={saved.receipt} initial={saved.tracking}
          onDone={() => { setReload(current => current + 1); onChanged?.(); }} /> :
          <ul>{saved.receipt.items.map(item => <li key={item.id}>{item.original} · {item.total} {saved.receipt?.currency}</li>)}</ul>}
        {saved.receipt.categorySplit && saved.receipt.categorySplit.mode !== "off" && <details>
          <summary>Reviewed item categories</summary>
          <Suspense fallback={null}><ReceiptCategoryBreakdown receipt={saved.receipt} receiptNumber={receipts.length > 1 ? index + 1 : undefined} /></Suspense>
          <ul aria-label="Reviewed receipt item categories">{saved.receipt.items.map(item => {
            const choice = saved.receipt!.categorySplit!.assignments[item.id];
            return <li key={item.id}>{item.name || item.original} · {[choice?.category, choice?.subcategory].filter(Boolean).join(" · ") || "Unassigned"}</li>;
          })}</ul>
          <ul>{saved.receipt.categorySplit.adjustments.map(row => <li key={row.id}>{row.label || row.kind} · {row.amount || "Check amount"} {saved.receipt?.currency}{row.included ? " · already in item prices" : ""}</li>)}</ul>
        </details>}
        {onUseDetails && <button type="button" disabled={disabled} onClick={() => onUseDetails(saved.receipt!)}>Review transaction with receipt details</button>}
      </details>}
      {transaction && !saved.receipt?.items.length && !!saved.images?.length && !form &&
        <button type="button" disabled={disabled} onClick={() => setForm({ receiptId: saved.id || saved.receipt?.id })}>Extract receipt data{receipts.length > 1 ? ` · Receipt ${index + 1}` : ""}</button>}
    </section>)}
    {form && transaction && onBusyChange && <TransactionReceiptForm key={form.receiptId || "new"}
      transaction={transaction} sourceReceiptId={form.receiptId} onBusyChange={onBusyChange} entryOptions={entryOptions}
      onCancel={() => setForm(null)} onSaved={() => { setForm(null); setReload(current => current + 1); onChanged?.(); }} />}
  </>;
}
